import { PROPERTIES_SELECTION } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.constants';
import { extractPropertiesMultiSelectFields } from 'components/forms/domain/property/PropertyMultiSelectFilterField/PropertyMultiSelectFilterField.utils';
import useAppModal from 'hooks/useAppModal';
import { SeasonalRule } from 'models/seasonality/SeasonalRule';
import usePropertySettingsInitData from 'pages/property/usePropertySettingsInitData';
import DeleteSeasonalRuleModal from './DeleteSeasonalRuleModal';
import {
  APPLIES_TO_FIELD_NAME,
  deleteSeasonalRuleModalId,
} from './DeleteSeasonalRuleModal.constants';
import { DeleteSeasonalRuleFormValues } from './DeleteSeasonalRuleModal.schema';
import useDeleteSeasonalRuleMutation from './useDeleteSeasonalRuleMutation';

const useDeleteSeasonalRuleModal = ({ rule }: { rule: SeasonalRule }) => {
  const { property, allProperties } = usePropertySettingsInitData();
  const { openModal, closeModal } = useAppModal();
  const mutationKey: (string | number)[] = ['delete-seasonal-rule', rule.uid];

  const { deleteRule } = useDeleteSeasonalRuleMutation({
    mutationKey,
    successCallback: () => closeModal(deleteSeasonalRuleModalId),
  });

  const openDeleteSeasonalRuleModal = () => {
    const handleDelete = async (values: DeleteSeasonalRuleFormValues) => {
      const { updateAllProperties, propertiesUids } =
        extractPropertiesMultiSelectFields(APPLIES_TO_FIELD_NAME, values);

      const totalProperties = allProperties?.length ?? 0;
      const effectivelyAll =
        updateAllProperties ||
        (totalProperties > 0 && propertiesUids?.length === totalProperties);

      await deleteRule({
        uid: rule.uid,
        payload: {
          applyToAll: effectivelyAll,
          ...(effectivelyAll ? {} : { propertyUids: propertiesUids ?? [] }),
        },
      });
    };

    const defaultValues: DeleteSeasonalRuleFormValues = {
      appliesTo: PROPERTIES_SELECTION.SELECTED_PROPERTIES,
      appliesToProperties: [
        {
          isFixed: true,
          label: property?.name,
          value: property?.uid,
        },
      ],
    };

    openModal({
      id: deleteSeasonalRuleModalId,
      customContent: (
        <DeleteSeasonalRuleModal
          ruleName={rule.name}
          onSubmit={handleDelete}
          defaultValues={defaultValues}
          mutationKey={mutationKey}
        />
      ),
    });
  };

  return { openDeleteSeasonalRuleModal };
};

export default useDeleteSeasonalRuleModal;
